import { useEffect, useState } from "react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { PageLoading, ErrorState } from "../components/States";
import { api } from "../lib/api";
import { LEGAL, legalValue, isMissing } from "../lib/legal";
import { setPageTitle } from "../lib/format";

// Renders a backend-canonical legal document (Terms / Privacy / Commercial Host
// Agreement): the server owns the text and version so web, email and any future
// client always render the same approved document.
function ApiDoc({ title, fetcher, testid }) {
  const [doc, setDoc] = useState(null);
  const [err, setErr] = useState(null);
  useEffect(() => { setPageTitle(title); fetcher().then(setDoc).catch((e) => setErr(e.message)); }, []); // eslint-disable-line
  return (
    <div className="card-dark p-6 sm:p-10" data-testid={testid}>
      <h1 className="text-3xl font-extrabold tracking-tight">{title}</h1>
      <p className="mt-2 text-xs text-zinc-500">Version {doc?.version || "…"} · {LEGAL.copyright}</p>
      {err ? <ErrorState body={err} /> : !doc ? <PageLoading label={`Loading ${title.toLowerCase()}…`} /> : (
        <div className="mt-8 space-y-6">
          {doc.content.split(/\n\n/).map((block, i) => {
            const nl = block.indexOf("\n");
            const head = nl > 0 ? block.slice(0, nl) : block;
            const rest = nl > 0 ? block.slice(nl + 1) : "";
            return (
              <section key={i}>
                {i > 0 && <h2 className="text-[#FF3B45] font-bold text-sm uppercase tracking-wide">{head}</h2>}
                {rest && <p className="mt-2 text-sm text-zinc-300 leading-relaxed whitespace-pre-line">{rest}</p>}
              </section>
            );
          })}
        </div>
      )}
    </div>
  );
}

function TermsDoc() {
  return <ApiDoc title="Terms of Service" fetcher={api.terms} testid="terms-content" />;
}

function PrivacyDoc() {
  return <ApiDoc title="Privacy Policy" fetcher={api.privacy} testid="privacy-content" />;
}

function CommercialTermsDoc() {
  return <ApiDoc title="Commercial Host Agreement" fetcher={api.commercialTerms} testid="commercial-terms-content" />;
}

function LegalDoc() {
  useEffect(() => setPageTitle("Legal"), []);
  // Customer-facing corporate identity only — the corporation number is
  // confidential and deliberately NOT displayed (owner directive 2026-10-14).
  const rows = [
    ["Brand", LEGAL.brand],
    ["Operator", LEGAL.operator],
    ["Company legal name", LEGAL.companyLegalName],
    ["Incorporation", LEGAL.incorporation],
    ["Registered address", LEGAL.companyAddress],
    ["Governing law", LEGAL.governingLaw],
    ["Dispute venue", LEGAL.disputeVenue],
    ["Legal email", LEGAL.legalEmail],
    ["Privacy email", LEGAL.privacyEmail],
    ["Support email", LEGAL.supportEmail],
    ["Terms effective date", LEGAL.effectiveDate],
  ];
  return (
    <div className="card-dark p-6 sm:p-10" data-testid="legal-content">
      <h1 className="text-3xl font-extrabold tracking-tight">Legal Information</h1>
      <p className="mt-2 text-sm text-zinc-400">{LEGAL.poweredBy}</p>
      <div className="mt-8 divide-y divide-[#27272A]">
        {rows.map(([k, v]) => (
          <div key={k} className="py-3.5 grid sm:grid-cols-3 gap-1 sm:gap-4">
            <dt className="text-xs font-semibold uppercase tracking-widest text-zinc-400">{k}</dt>
            <dd className={`sm:col-span-2 text-sm ${isMissing(v) ? "text-amber-400" : "text-zinc-200"}`}
                data-testid={`legal-${k.toLowerCase().replace(/\s+/g, "-")}`}>{legalValue(v)}</dd>
          </div>
        ))}
      </div>
    </div>
  );
}

function Shell({ children }) {
  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />
      <main className="flex-1 max-w-4xl w-full mx-auto px-5 sm:px-8 pt-28 pb-16">{children}</main>
      <Footer />
    </div>
  );
}

export function TermsPage() { return <Shell><TermsDoc /></Shell>; }
export function PrivacyPage() { return <Shell><PrivacyDoc /></Shell>; }
export function LegalPage() { return <Shell><LegalDoc /></Shell>; }
export function CommercialTermsPage() { return <Shell><CommercialTermsDoc /></Shell>; }
